import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  ImageUploaderComponent,
  UploadedImage,
} from '@components/app-image-uploader/image-uploader.component';

import {
  Category,
  CreateCategoryDto,
  UpdateCategoryDto,
} from '@services/categories/interfaces/categories.interfaces';
import {
  MODAL_DATA,
  MODAL_REF,
  ModalRef,
} from '@services/components/ModalService/modal.ref';

import { CDN_CATEGORIES_URL } from '@constants/constants';
import { MERCHANDISING_CATEGORY_ID } from '@constants/merchandising.constants';

export interface CategoryFormModalData {
  allCategories: Category[];
  category?: Category;
  presetParent?: Category;
}

export type CategoryFormModalResult =
  | { mode: 'create'; payload: CreateCategoryDto; imageFile: File | null }
  | { mode: 'update'; id: string; payload: UpdateCategoryDto; imageFile: File | null };

@Component({
  selector: 'app-category-form-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, ImageUploaderComponent],
  templateUrl: './category-form-modal.component.html',
  styleUrl: './category-form-modal.component.css',
})
export class CategoryFormModalComponent {
  private readonly modalRef = inject(MODAL_REF) as ModalRef<CategoryFormModalResult>;
  private readonly data = inject<CategoryFormModalData>(MODAL_DATA);
  private readonly fb = inject(FormBuilder);

  public readonly isEditing = !!this.data?.category;
  public readonly isEditingSubcategory = !!this.data?.category?.parentId;
  public readonly presetParent = this.data?.presetParent;
  public readonly isMerchandisingRoot = this.data?.category?.id === MERCHANDISING_CATEGORY_ID;
  public readonly isMerchandisingSubcategory = this.data?.category?.parentId === MERCHANDISING_CATEGORY_ID;

  private readonly imageFile = signal<File | null>(null);
  private readonly hasNewImage = signal(false);

  public readonly form = this.fb.nonNullable.group({
    name: [
      { value: this.data?.category?.name ?? '', disabled: this.isMerchandisingRoot },
      [Validators.required, Validators.maxLength(80)],
    ],
    parentId: [{ value: this.data?.category?.parentId ?? '', disabled: this.isMerchandisingSubcategory }],
  });

  public readonly parentOptions = computed(() => {
    const all = this.data?.allCategories ?? [];
    if (!this.isEditing) {
      return all.filter((c) => !c.parentId);
    }
    const editing = this.data.category;
    if (!editing) {
      return all.filter((c) => !c.parentId);
    }
    const editingId = editing.id;
    const banned = new Set<string>([editingId]);
    let added = true;
    while (added) {
      added = false;
      all.forEach((c) => {
        if (c.parentId && banned.has(c.parentId) && !banned.has(c.id)) {
          banned.add(c.id);
          added = true;
        }
      });
    }
    // The backend rejects moving existing categories into the Merchandising tree.
    banned.add(MERCHANDISING_CATEGORY_ID);
    return all.filter((c) => !c.parentId && !banned.has(c.id));
  });

  public readonly hasParentOptions = computed(() => this.parentOptions().length > 0);

  public readonly initialImageUrls = this.data?.category?.imageUrl
    ? [{ url: `${CDN_CATEGORIES_URL}${this.data.category.imageUrl}`, fileSize: this.data.category.imageFileSize ?? null }]
    : [];

  public readonly nameInvalid = computed(() => {
    const ctrl = this.form.controls.name;
    return ctrl.invalid && (ctrl.touched || ctrl.dirty);
  });

  public onImagesChange(images: UploadedImage[]): void {
    const first = images[0];
    this.imageFile.set(first?.file ?? null);
    this.hasNewImage.set(images.length > 0);
  }

  public onCancel(): void {
    this.modalRef.close({ reason: 'cancel' });
  }

  public onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { name, parentId } = this.form.getRawValue();

    if (this.isEditing && this.data.category) {
      const normalizedParentId = parentId ? parentId : null;
      const payload: UpdateCategoryDto = {
        name: name.trim(),
        parentId: normalizedParentId,
      };
      this.modalRef.close({
        reason: 'confirm',
        data: {
          mode: 'update',
          id: this.data.category.id,
          payload,
          imageFile: this.imageFile(),
        },
      });
      return;
    }

    const payload: CreateCategoryDto = {
      name: name.trim(),
      parentId: this.presetParent?.id ?? null,
    };
    this.modalRef.close({
      reason: 'confirm',
      data: {
        mode: 'create',
        payload,
        imageFile: this.imageFile(),
      },
    });
  }
}
